import React, { useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import LinearProgress from '@mui/material/LinearProgress'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useSelector, useDispatch } from 'react-redux'
import { fetchSearchMetadata } from '../../betasearch/store/search/searchSlice'
import {
  toggleGoldStandardData,
  updateBulkMetadata,
} from '../../store/metadata/actionCreator'
import { getProjectAssetsWithFilters } from '../../store/projects/actionCreator'

const ClassificationCard = ({
  selectedAssetGoldMetadata = {},
  bulkEditAssetIds = [],
  banner = false,
  lanId = '',
  consume = '',
  view = '',
  handleCallBackResponse = () => { },
  onClassificationChange = () => { },
  onClassificationSubmit = () => { },
  updateParentState = () => { },
}) => {
  const dispatch = useDispatch()
  const classificationFacetRecords = useSelector(
    (state) => state.newSearch?.classificationFacets?.records || [],
  )
  const pagination = useSelector((state) => state.projects?.pagination)
  const filters = useSelector((state) => state.projects?.filters)

  // Transform flat asset_level_details records into hierarchical dropdown format
  const classificationList = React.useMemo(() => {
    const grouped = {}
    classificationFacetRecords.forEach((record) => {
      const { category = '', value = '', id = '' } = record
      if (!category) return
      if (!grouped[category]) {
        grouped[category] = { classification_name: category, asset_types: [] }
      }
      grouped[category].asset_types.push({ name: value, classification_id: id })
    })
    return Object.values(grouped)
  }, [classificationFacetRecords])

  const [classification, setClassification] = useState('')
  const [assetType, setAssetType] = useState('')
  const [filteredAssetTypes, setFilteredAssetTypes] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialClassification, setInitialClassification] = useState('')
  const [initialAssetType, setInitialAssetType] = useState('')

  const filteredClassificationSelectorTypes = classificationList.map(
    (classificationDetail) => {
      if (classificationDetail.classification_name === 'Design File') {
        return {
          ...classificationDetail,
          asset_types: classificationDetail.asset_types.filter(
            (asset) =>
              asset.name !== 'Dieline' &&
              asset.name !== 'Icon' &&
              asset.name !== 'Template' &&
              asset.name !== 'Illustration',
          ),
        }
      } else if (
        classificationDetail.classification_name === 'Non-Product Image'
      ) {
        return {
          ...classificationDetail,
          asset_types: classificationDetail.asset_types.filter(
            (asset) => asset.name !== 'Set' && asset.name !== 'Events',
          ),
        }
      } else if (classificationDetail.classification_name === 'Product Image') {
        return {
          ...classificationDetail,
          asset_types: classificationDetail.asset_types.filter(
            (asset) => asset.name !== 'Infographic',
          ),
        }
      } else if (
        classificationDetail.classification_name === 'Non-Product Motion'
      ) {
        return {
          ...classificationDetail,
          asset_types: classificationDetail.asset_types.filter(
            (asset) =>
              asset.name !== 'Outline with talent' &&
              asset.name === 'Outline without talent' &&
              asset.name === 'Environment with talent' &&
              asset.name === 'Environment without talent',
          ),
        }
      } else if (
        classificationDetail.classification_name === 'Product Motion'
      ) {
        return {
          ...classificationDetail,
          asset_types: classificationDetail.asset_types.filter(
            (asset) =>
              asset.name !== 'Outline with talent' &&
              asset.name === 'Outline without talent' &&
              asset.name === 'Environment with talent' &&
              asset.name !== 'Environment without talent',
          ),
        }
      }
      return classificationDetail
    },
  )

  useEffect(() => {
    // Initialize classification and asset type from selectedAssetGoldMetadata
    const asset_type =
      selectedAssetGoldMetadata?.metadata?.asset_classification?.asset_type ||
      ''
    const secondary_classification =
      selectedAssetGoldMetadata?.metadata?.asset_classification
        ?.secondary_classification || ''

    setAssetType(asset_type)
    setClassification(secondary_classification)
    setInitialAssetType(asset_type)
    setInitialClassification(secondary_classification)
  }, [selectedAssetGoldMetadata])

  // Fetch classification data from asset_level_details API if not already loaded
  useEffect(() => {
    if (classificationFacetRecords.length === 0) {
      dispatch(fetchSearchMetadata())
    }
  }, [dispatch, classificationFacetRecords.length])

  useEffect(() => {
    // Filter asset types based on the selected classification
    const selectedClassification = filteredClassificationSelectorTypes.find(
      (item) => item.classification_name === classification,
    )
    setFilteredAssetTypes(selectedClassification?.asset_types || [])
  }, [classification, classificationList])

  // Track classification changes and notify parent
  useEffect(() => {
    const hasChanges =
      (classification !== initialClassification || assetType !== initialAssetType) &&
      classification.length > 0 &&
      assetType.length > 0
    onClassificationChange(hasChanges)
  }, [classification, assetType, initialClassification, initialAssetType, onClassificationChange])

  // Sync classification_id back to parent metadataState only when user changes values
  useEffect(() => {
    // Only sync when there's an actual user-driven change (not initial load)
    const hasChanges =
      (classification !== initialClassification || assetType !== initialAssetType) &&
      classification.length > 0 &&
      assetType.length > 0

    if (hasChanges && filteredClassificationSelectorTypes.length > 0) {
      const selectedClassification = filteredClassificationSelectorTypes.find(
        (item) => item.classification_name === classification,
      )
      const selectedAssetTypeObj = (selectedClassification?.asset_types || []).find(
        (item) => item.name === assetType,
      )
      const classificationId = selectedAssetTypeObj?.classification_id || ''
      updateParentState({
        assetClassifications: {
          classifications_name: classification,
          asset_type: {
            name: assetType,
            id: classificationId,
          },
        },
      })
    }
  }, [classification, assetType, initialClassification, initialAssetType, filteredClassificationSelectorTypes])

  const handleCallBack = (response, responseType, searchView) => {
    setLoading(false)
    const payload = {
      asset_uoi_id: response[0].asset_id,
    }
    handleCallBackResponse(response)
    onClassificationSubmit()
    // Update initial values after successful submit
    setInitialClassification(classification)
    setInitialAssetType(assetType)
    dispatch(toggleGoldStandardData(payload))
    dispatch(
      getProjectAssetsWithFilters(
        selectedAssetGoldMetadata.metadata.project_info.project_uuid,
        pagination,
        filters,
      ),
    )
  }

  const handleSubmitClassification = () => {
    const classificationId = filteredAssetTypes.find(
      (item) => item.name === assetType,
    )
    let payload
    if (bulkEditAssetIds.length > 1) {
      payload = bulkEditAssetIds.map((id) => ({
        asset_id: id,
        update_data: {
          classification_id: classificationId.classification_id ?? ' ',
        },
      }))
    } else {
      payload = [
        {
          asset_id: selectedAssetGoldMetadata.asset_id,
          update_data: {
            classification_id: classificationId.classification_id ?? ' ',
            file_name:
              selectedAssetGoldMetadata?.metadata?.file_metadata?.file_name ?? '',
            librarian_review_status:
              selectedAssetGoldMetadata?.librarian_review_status ?? '',
          },
        },
      ]
    }
    setLoading(true)
    dispatch(updateBulkMetadata(payload, lanId, handleCallBack, consume, view))
  }

  return (
    <>
      <Accordion
        defaultExpanded
        sx={{
          marginBottom: '10px',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="classification-content"
          id="classification-header"
        >
          <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
            Asset Classification
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container gap="10px" xs={12}>
            {/* Classification Select */}
            <Grid item xs={6}>
              <FormControl fullWidth sx={{ marginBottom: '1rem' }}>
                <InputLabel id="classification-label">
                  Classification
                </InputLabel>
                <Select
                  labelId="classification-label"
                  id="classification-select"
                  label="Classification"
                  value={classification}
                  onChange={(event) => setClassification(event.target.value)}
                  fullWidth
                  data-cy="AddingClassification"
                >
                  {filteredClassificationSelectorTypes.map(
                    (classificationDetail, classificationIndex) => {
                      const { classification_name = '', asset_types = [] } =
                        classificationDetail
                      if (
                        asset_types.length === 0 &&
                        classification_name !== 'Upload without classification'
                      ) {
                        return null
                      }
                      return (
                        <MenuItem
                          key={classification_name}
                          value={classification_name}
                        >
                          {classification_name}
                        </MenuItem>
                      )
                    },
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Asset Type Select */}
            <Grid item xs={5}>
              <FormControl fullWidth>
                <InputLabel id="asset-type-label">Asset Type</InputLabel>
                <Select
                  labelId="asset-type-label"
                  id="asset-type-select"
                  label="Asset Type"
                  value={assetType}
                  data-cy="AddingAssetType"
                  onChange={(event) => setAssetType(event.target.value)}
                  fullWidth
                  disabled={!filteredAssetTypes.length} // Disable if no asset types are available
                >
                  {filteredAssetTypes.map((type) => (
                    <MenuItem key={type.classification_id} value={type.name}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {
            bulkEditAssetIds.length === 1 && (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#0288D1',
                  marginTop: '1rem',
                  '&:hover': {
                    backgroundColor: '#0277BD',
                  },
                }}
                onClick={handleSubmitClassification}
                data-cy="saveClassification"
                disabled={
                  classification.length === 0 ||
                  assetType.length === 0 ||
                  (classification === initialClassification && assetType === initialAssetType)
                }
              >
                {classification.length > 0 ? 'Update Classification' : 'Save'} <ChevronRight />
              </Button>
            )
          }
          {loading && (
            <LinearProgress
              sx={{
                mt: '0.5rem',
                backgroundColor: '#0288D1',
                color: '#0288D1',
              }}
            />
          )}
        </AccordionDetails>
      </Accordion>

      {banner === null && (
        <div style={{ margin: '10px 0 0 0' }}>
          <Alert severity="warning" className="banner-sub">
            <div className="banner-sub-div">
              <div>
                <AlertTitle>
                  Metadata information varies according to the classification
                </AlertTitle>
                <p style={{ padding: 0, margin: 0 }}>
                  We will be sharing all other required metadata details once
                  the classification is added above based on the file
                  information and classification.
                </p>
              </div>
            </div>
          </Alert>
        </div>
      )}
    </>
  )
}

export default ClassificationCard
